/**
 * HATCH entity → `RenderableEntity` adapter (PR-D).
 *
 * Reads the boundary loops + pattern metadata from the EntityPreview's
 * properties (populated by `buildHatchPreview` in dxfGeometry.ts), then
 * delegates to the shared {@link buildHatchSceneGeometry} builder
 * (`./geom/hatchGeometry.ts`) which walks the boundary into polygonal
 * `HatchLoop`s and emits the `kind: "hatch"` SceneGeometry. Origin offset
 * + elevation lift are applied per-vertex inside the builder's emitter
 * (Q1.A: every coord that reaches the GPU is in offset-world space).
 *
 * # DEFUSE Direction-3 — shared builder
 *
 * The boundary-walk + emitter + `kind: "hatch"` assembly + bbox math now
 * live ONCE in `./geom/hatchGeometry.ts`, shared with the typed
 * `./typed/hatchFromEntity.ts` adapter. This file keeps ONLY the string-bag
 * value extraction (Pattern / Scale / Angle / Solid / Elevation +
 * `JSON.parse(__hatch_boundary_paths)` + `parseSceneGradient(__hatch_gradient)`)
 * and feeds those already-parsed primitives to the builder. Behaviour-preserving.
 *
 * # Why JSON-encoded boundary in EntityPreview
 *
 * `EntityPreview.properties` is `Record<string, string>` — the SVG
 * renderer's existing surface. `buildHatchPreview` serializes the
 * underlying `HatchEntity.boundaryPaths` into a `__hatch_boundary_paths`
 * JSON string so this adapter has access to the full edge graph
 * without leaking model-types into the contracts package. (Mirrors
 * the `__entity_annotative_contexts` / `__mleader_leader_roots`
 * channels already used by other adapters.)
 *
 * # Edge cases
 *
 *   - `loops.length === 0` → adapter still emits a renderable with
 *     empty loops; renderer skips drawing (CLAUDE.md PR-D §8 edge case
 *     1). Bounds default to a degenerate point so the entity is still
 *     in the spatial index for select-by-id.
 *   - Solid hatch → `solid: true`, pattern name forced to "SOLID".
 *   - Custom DXF pattern → name preserved verbatim; renderer falls
 *     back to a fully-transparent texture if the name is unknown.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import type { HatchBoundaryPath } from "@dxf-ai-editor/drawing-model";

import { readNumber } from "../../scene/renderHelpers";
import type { RenderableEntity, SceneGradient } from "../types";

import { buildRenderableShell, type AdapterContext } from "./adapterContext";
import { buildHatchSceneGeometry } from "./geom/hatchGeometry";

export function hatchToRenderable(
  entity: EntityPreview,
  ctx: AdapterContext,
): RenderableEntity {
  const patternName = String(entity.properties.Pattern ?? "ANSI31");
  const patternScale = readNumber(entity.properties, "Scale", 1);
  // Pattern angle is stored in degrees in the property bag; the shared builder
  // does the single deg → rad conversion before the angle reaches the geometry.
  const patternAngleDeg = readNumber(entity.properties, "Angle", 0);
  const solid =
    String(entity.properties.Solid ?? "").toLowerCase() === "yes" ||
    patternName.toUpperCase() === "SOLID";
  const elevation = readNumber(entity.properties, "Elevation", 0);

  const boundaryJson = entity.properties.__hatch_boundary_paths ?? "[]";
  let parsedPaths: HatchBoundaryPath[] = [];
  try {
    const parsed = JSON.parse(boundaryJson);
    if (Array.isArray(parsed)) parsedPaths = parsed as HatchBoundaryPath[];
  } catch {
    parsedPaths = [];
  }

  // Slice 2 — gradient ramp channel (present ONLY for gradient hatches; absent
  // → undefined → the existing solid / pattern paths). Parsed defensively like
  // __hatch_boundary_paths (a malformed JSON or a non-object payload leaves the
  // hatch gradient-less rather than crashing the adapter).
  const gradient = parseSceneGradient(entity.properties.__hatch_gradient);

  const { geometry, bounds } = buildHatchSceneGeometry({
    boundaryPaths: parsedPaths,
    patternName,
    patternScale,
    patternAngleDeg,
    solid,
    elevation,
    gradient,
  });

  return buildRenderableShell(entity, ctx, geometry, bounds);
}

/**
 * Slice 2 — parse the `__hatch_gradient` channel (mirrors the
 * `__hatch_boundary_paths` try/catch). Returns the {@link SceneGradient} for a
 * gradient hatch, or `undefined` when the channel is absent / malformed / not a
 * plain object — so a defensive failure leaves the hatch on its existing solid /
 * pattern path rather than crashing the adapter.
 */
function parseSceneGradient(raw: string | undefined): SceneGradient | undefined {
  if (raw === undefined) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed !== null &&
      typeof parsed === "object" &&
      !Array.isArray(parsed) &&
      typeof (parsed as SceneGradient).color0Hex === "string" &&
      typeof (parsed as SceneGradient).color1Hex === "string" &&
      ((parsed as SceneGradient).shadingType === "axial" ||
        (parsed as SceneGradient).shadingType === "radial")
    ) {
      return parsed as SceneGradient;
    }
  } catch {
    return undefined;
  }
  return undefined;
}
